<template>
  <section class="program-hero" aria-label="Probabilistic Boxscore Hero">
    <div class="program-hero__inner">
      <p class="eyebrow program-hero__kicker">Tonight's Game &mdash; A Simulated Program</p>

      <div v-if="game" class="program-hero__matchup">
        <div class="team team--away">
          <span class="team__label">Away</span>
          <span class="team__abbr numeric">{{ game.away_abbr }}</span>
          <span class="team__name">{{ game.away_team }}</span>
        </div>

        <div class="program-hero__versus" aria-hidden="true">
          <span class="versus__rule"></span>
          <span class="versus__at">vs.</span>
          <span class="versus__rule"></span>
        </div>

        <div class="team team--home">
          <span class="team__label">Home</span>
          <span class="team__abbr numeric">{{ game.home_abbr }}</span>
          <span class="team__name">{{ game.home_team }}</span>
        </div>
      </div>

      <div v-if="game" class="program-hero__byline">
        <span class="byline__rule"></span>
        <span class="byline__text">{{ formattedDate }} &middot; First Pitch Preview</span>
        <span class="byline__rule"></span>
      </div>

      <h1 class="program-hero__headline">See the game <em>before</em> it happens.</h1>

      <p class="program-hero__lede">
        Thousands of simulations. Probability distributions. The complete picture of what could
        unfold, measured inning by inning and plate appearance by plate appearance.
      </p>

      <p class="program-hero__cue">
        <span class="cue__mark">♦</span>
        Turn the page for the line score
        <span class="cue__mark">♦</span>
      </p>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Game {
  home_team: string
  home_abbr: string
  away_team: string
  away_abbr: string
  date: string
}

const props = withDefaults(defineProps<{ game?: Game }>(), { game: undefined })

const formattedDate = computed((): string => {
  if (!props.game?.date) return ''
  const d = new Date(props.game.date)
  if (Number.isNaN(d.getTime())) return props.game.date.toUpperCase()
  return d
    .toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    .toUpperCase()
})
</script>

<style scoped>
.program-hero {
  max-width: 1280px;
  margin: 0 auto;
  padding: 4rem 2rem 3.5rem;
  position: relative;
  text-align: center;
}

.program-hero::before {
  /* ghost baseball in the corner */
  content: '';
  position: absolute;
  top: -1rem;
  right: -4rem;
  width: 360px;
  height: 360px;
  border: var(--rule-thin) solid var(--ink-20);
  border-radius: 50%;
  background: radial-gradient(
    circle at 30% 30%,
    transparent 0%,
    transparent 49%,
    var(--ink-10) 50%,
    transparent 52%
  );
  pointer-events: none;
  z-index: 0;
}

.program-hero__inner {
  position: relative;
  z-index: 1;
  max-width: 880px;
  margin: 0 auto;
}

.program-hero__kicker {
  margin-bottom: 2rem;
  justify-content: center;
}

/* ─── Matchup block ──────────────────────────────────────────────── */
.program-hero__matchup {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 2rem;
  margin-bottom: 1.75rem;
}

.team {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
}

.team__label {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.24em;
  color: var(--ox);
}

.team__abbr {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 144,
    'wght' 800;
  font-size: clamp(3rem, 8vw, 5.5rem);
  line-height: 0.9;
  color: var(--ink);
  letter-spacing: -0.03em;
}

.team--away .team__abbr {
  font-style: italic;
  color: var(--ox);
}

.team__name {
  font-family: var(--font-editorial);
  font-size: 0.95rem;
  font-style: italic;
  color: var(--ink-medium);
}

.program-hero__versus {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  min-width: 3rem;
}

.versus__rule {
  width: 2rem;
  height: 1px;
  background: var(--ink);
}

.versus__at {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 48,
    'wght' 500;
  font-size: 1.4rem;
  font-style: italic;
  color: var(--ink-medium);
}

/* ─── Byline ──────────────────────────────────────────────────────── */
.program-hero__byline {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.byline__rule {
  flex: 0 1 80px;
  height: 1px;
  background: var(--ink);
}

.byline__text {
  font-family: var(--font-ui);
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ink-medium);
  white-space: nowrap;
}

/* ─── Headline & lede ────────────────────────────────────────────── */
.program-hero__headline {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 144,
    'SOFT' 30,
    'wght' 700;
  font-size: clamp(2.5rem, 6vw, 4.75rem);
  line-height: 0.95;
  letter-spacing: -0.025em;
  color: var(--ink);
  margin-bottom: 1.5rem;
  max-width: 18ch;
  margin-left: auto;
  margin-right: auto;
}

.program-hero__headline em {
  font-style: italic;
  color: var(--ox);
  font-variation-settings:
    'opsz' 144,
    'SOFT' 100,
    'wght' 500;
}

.program-hero__lede {
  font-family: var(--font-editorial);
  font-size: 1.15rem;
  line-height: 1.6;
  color: var(--ink-medium);
  max-width: 52ch;
  margin: 0 auto 2.25rem;
}

.program-hero__cue {
  font-family: var(--font-ui);
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ink-faint);
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
}

.cue__mark {
  color: var(--ox);
}

@media (max-width: 700px) {
  .program-hero {
    padding: 3rem 1rem 2.5rem;
  }

  .program-hero::before {
    display: none;
  }

  .program-hero__matchup {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .program-hero__versus {
    flex-direction: row;
    justify-content: center;
  }

  .versus__rule {
    width: 3rem;
  }

  .program-hero__byline {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .byline__rule {
    flex: 0 1 40px;
  }
}
</style>
